import { useState, useEffect } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { CheckCircle, Upload, Truck, Loader2, AlertCircle } from "lucide-react"
import { useLanguage } from "@/lib/i18n"
import { applicationSchema, type ApplicationFormData } from "@/lib/types"
import { apiClient } from "@/lib/api"
import type { Vacancy } from "@shared/types"

interface ApplicationFormProps {
  vacancyId?: number | null
  vacancies?: Vacancy[]
}

export default function ApplicationForm({ vacancyId, vacancies = [] }: ApplicationFormProps) {
  const { t, language } = useLanguage()
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [hasEndorsements, setHasEndorsements] = useState(false)

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ApplicationFormData>({
    resolver: zodResolver(applicationSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      city: "",
      state: "",
      cdlClass: undefined,
      yearsExperience: undefined,
      endorsements: [],
      equipmentExperience: [],
      accidentsViolations: "",
      drugTestConsent: false,
      backgroundCheckConsent: false,
      vacancyId: vacancyId ?? undefined,
    },
  })

  useEffect(() => {
    if (vacancyId) {
      setValue("vacancyId", vacancyId)
    }
  }, [vacancyId, setValue])

  const drugTestConsent = watch("drugTestConsent")
  const backgroundCheckConsent = watch("backgroundCheckConsent")
  const selectedState = watch("state")
  const selectedCdlClass = watch("cdlClass")
  const selectedVacancyId = watch("vacancyId")

  const getVacancyTitle = (v: Vacancy) =>
    language === "es" && v.titleEs ? v.titleEs : v.title

  const onSubmit = async (data: ApplicationFormData) => {
    setSubmitError(null)
    try {
      await apiClient.applications.submit(data)
      setIsSubmitted(true)
      reset()
    } catch (error) {
      console.error("Application submission error:", error)
      setSubmitError(
        error instanceof Error ? error.message : "Failed to submit application"
      )
    }
  }

  if (isSubmitted) {
    return (
      <section id="apply" className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <Card className="max-w-2xl mx-auto bg-card border-border text-center">
            <CardContent className="py-12">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                <CheckCircle className="h-10 w-10 text-primary" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-card-foreground">
                {t("apply.success.title")}
              </h3>
              <p className="text-muted-foreground mb-6">
                {t("apply.success.desc")}
              </p>
              <Button onClick={() => setIsSubmitted(false)}>
                {t("apply.submitAnother")}
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>
    )
  }

  return (
    <section id="apply" className="py-20 bg-background">
      <div className="container mx-auto px-4">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <p className="text-primary font-semibold tracking-wider uppercase mb-4">
            {t("apply.tagline")}
          </p>
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-balance">
            {t("apply.title")}
          </h2>
          <p className="text-muted-foreground text-lg leading-relaxed">
            {t("apply.description")}
          </p>
        </div>

        <Card className="max-w-4xl mx-auto bg-card border-border">
          <CardHeader className="border-b border-border">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Truck className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle className="text-card-foreground">
                  {t("apply.commission")}
                </CardTitle>
                <CardDescription>{t("apply.required")}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
              {/* Error Alert */}
              {submitError && (
                <div className="bg-destructive/10 border border-destructive/50 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-destructive">
                      Submission Failed
                    </p>
                    <p className="text-sm text-destructive/80">{submitError}</p>
                  </div>
                </div>
              )}

              {/* Position Selection */}
              {vacancies.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-4 text-primary">
                    {t("apply.selectPosition")}
                  </h3>
                  <Select
                    value={selectedVacancyId ? String(selectedVacancyId) : "general"}
                    onValueChange={(value) =>
                      setValue("vacancyId", value === "general" ? undefined : parseInt(value))
                    }
                  >
                    <SelectTrigger className="bg-input border-border">
                      <SelectValue placeholder={t("apply.selectPosition")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">{t("apply.generalApplication")}</SelectItem>
                      {vacancies.map((v) => (
                        <SelectItem key={v.id} value={String(v.id)}>
                          {getVacancyTitle(v)}
                          {v.location ? ` — ${v.location}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Personal Information */}
              <div>
                <h3 className="text-lg font-semibold mb-4 text-primary">
                  {t("apply.personal")}
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName" className="text-card-foreground">
                      {t("apply.firstName")} *
                    </Label>
                    <Input
                      id="firstName"
                      {...register("firstName")}
                      placeholder="John"
                      className="bg-input border-border"
                    />
                    {errors.firstName && (
                      <p className="text-sm text-destructive">
                        {errors.firstName.message}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName" className="text-card-foreground">
                      {t("apply.lastName")} *
                    </Label>
                    <Input
                      id="lastName"
                      {...register("lastName")}
                      placeholder="Doe"
                      className="bg-input border-border"
                    />
                    {errors.lastName && (
                      <p className="text-sm text-destructive">
                        {errors.lastName.message}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-card-foreground">
                      {t("apply.email")} *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      {...register("email")}
                      placeholder="john.doe@email.com"
                      className="bg-input border-border"
                    />
                    {errors.email && (
                      <p className="text-sm text-destructive">
                        {errors.email.message}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-card-foreground">
                      {t("apply.phone")} *
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      {...register("phone")}
                      placeholder="(555) 123-4567"
                      className="bg-input border-border"
                    />
                    {errors.phone && (
                      <p className="text-sm text-destructive">
                        {errors.phone.message}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city" className="text-card-foreground">
                      {t("apply.city")}
                    </Label>
                    <Input
                      id="city"
                      {...register("city")}
                      placeholder="Deptford"
                      className="bg-input border-border"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state" className="text-card-foreground">
                      {t("apply.state")}
                    </Label>
                    <Select
                      value={selectedState}
                      onValueChange={(value) => setValue("state", value)}
                    >
                      <SelectTrigger className="bg-input border-border">
                        <SelectValue placeholder={t("apply.selectState")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NJ">New Jersey</SelectItem>
                        <SelectItem value="PA">Pennsylvania</SelectItem>
                        <SelectItem value="NY">New York</SelectItem>
                        <SelectItem value="DE">Delaware</SelectItem>
                        <SelectItem value="MD">Maryland</SelectItem>
                        <SelectItem value="VA">Virginia</SelectItem>
                        <SelectItem value="CT">Connecticut</SelectItem>
                        <SelectItem value="MA">Massachusetts</SelectItem>
                        <SelectItem value="FL">Florida</SelectItem>
                        <SelectItem value="TX">Texas</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Driving Experience */}
              <div>
                <h3 className="text-lg font-semibold mb-4 text-primary">
                  {t("apply.experience")}
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cdlClass" className="text-card-foreground">
                      {t("apply.cdlClass")}
                    </Label>
                    <Select
                      value={selectedCdlClass}
                      onValueChange={(value: "A" | "B" | "C") =>
                        setValue("cdlClass", value)
                      }
                    >
                      <SelectTrigger className="bg-input border-border">
                        <SelectValue placeholder={t("apply.selectCdl")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="A">{t("apply.classA")}</SelectItem>
                        <SelectItem value="B">{t("apply.classB")}</SelectItem>
                        <SelectItem value="C">Class C CDL</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label
                      htmlFor="yearsExperience"
                      className="text-card-foreground"
                    >
                      {t("apply.yearsExp")}
                    </Label>
                    <Input
                      id="yearsExperience"
                      type="number"
                      min="0"
                      {...register("yearsExperience")}
                      placeholder="5"
                      className="bg-input border-border"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cdlNumber" className="text-card-foreground">
                      CDL Number
                    </Label>
                    <Input
                      id="cdlNumber"
                      {...register("cdlNumber")}
                      placeholder="CDL12345678"
                      className="bg-input border-border"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cdlState" className="text-card-foreground">
                      CDL State
                    </Label>
                    <Input
                      id="cdlState"
                      {...register("cdlState")}
                      placeholder="NJ"
                      className="bg-input border-border"
                    />
                  </div>
                </div>

                <div className="mt-4 space-y-4">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="hasEndorsements"
                      checked={hasEndorsements}
                      onCheckedChange={(checked) =>
                        setHasEndorsements(checked as boolean)
                      }
                    />
                    <Label
                      htmlFor="hasEndorsements"
                      className="text-sm text-muted-foreground"
                    >
                      {t("apply.endorsements")}
                    </Label>
                  </div>

                  {hasEndorsements && (
                    <div className="space-y-2 ml-6">
                      <Label
                        htmlFor="endorsementsList"
                        className="text-card-foreground"
                      >
                        {t("apply.listEndorsements")}
                      </Label>
                      <Input
                        id="endorsementsList"
                        placeholder="e.g., Hazmat, Tanker, Doubles/Triples"
                        className="bg-input border-border"
                        onChange={(e) => {
                          const endorsements = e.target.value
                            .split(",")
                            .map((s) => s.trim())
                            .filter(Boolean)
                          setValue("endorsements", endorsements)
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Previous Employment */}
              <div>
                <h3 className="text-lg font-semibold mb-4 text-primary">
                  Previous Employment
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label
                      htmlFor="previousEmployer"
                      className="text-card-foreground"
                    >
                      Previous Employer
                    </Label>
                    <Input
                      id="previousEmployer"
                      {...register("previousEmployer")}
                      placeholder="Company Name"
                      className="bg-input border-border"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label
                      htmlFor="previousEmployerPhone"
                      className="text-card-foreground"
                    >
                      Employer Phone
                    </Label>
                    <Input
                      id="previousEmployerPhone"
                      type="tel"
                      {...register("previousEmployerPhone")}
                      placeholder="(555) 123-4567"
                      className="bg-input border-border"
                    />
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div>
                <h3 className="text-lg font-semibold mb-4 text-primary">
                  {t("apply.additional")}
                </h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label
                      htmlFor="accidentsViolations"
                      className="text-card-foreground"
                    >
                      {t("apply.violations")}
                    </Label>
                    <Textarea
                      id="accidentsViolations"
                      {...register("accidentsViolations")}
                      placeholder={t("apply.violationsPlaceholder")}
                      rows={3}
                      className="bg-input border-border"
                    />
                  </div>
                </div>
              </div>

              {/* Document Upload Notice */}
              <div className="bg-muted/50 rounded-lg p-4 border border-border">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Upload className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium mb-1 text-card-foreground">
                      {t("apply.documents")}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t("apply.documentsDesc")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Consents */}
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="drugTestConsent"
                    checked={drugTestConsent}
                    onCheckedChange={(checked) =>
                      setValue("drugTestConsent", checked as boolean)
                    }
                  />
                  <Label
                    htmlFor="drugTestConsent"
                    className="text-sm text-muted-foreground"
                  >
                    I consent to pre-employment drug testing as required by DOT
                    regulations.
                  </Label>
                </div>
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="backgroundCheckConsent"
                    checked={backgroundCheckConsent}
                    onCheckedChange={(checked) =>
                      setValue("backgroundCheckConsent", checked as boolean)
                    }
                  />
                  <Label
                    htmlFor="backgroundCheckConsent"
                    className="text-sm text-muted-foreground"
                  >
                    {t("apply.terms")} *
                  </Label>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end gap-4">
                <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting || !backgroundCheckConsent}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    t("apply.submit")
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  )
}
